import { EntityIdSchema } from "@repo/core/universal";
import { z, SerializableSchema } from "@repo/utils";

export const InboxIdSchema = EntityIdSchema("inb");
export const ThreadIdSchema = EntityIdSchema("thr");
export const AttachmentIdSchema = EntityIdSchema("att");
export const MessageIdSchema = EntityIdSchema("msg");

export const ProviderSchema = z.enum(["GMAIL", "EXCHANGE", "EWS"]);

export const GmailSyncInfoSchema = z.object({
    mode: z.enum(["full", "partial"]),
    nextPageToken: z.nullable(z.string()),
    lastHistoryId: z.nullable(z.union([z.string(), z.number()])),
    labelIds: z.nullable(z.array(z.string())),
});

export const ExchangeSyncInfoSchema = z.object({
    nextLinks: z.nullable(z.record(z.string(), z.nullable(z.string()))),
    lastFoldersSyncAt: z.nullable(z.string()),
    folderIds: z.nullable(z.array(z.string())),
    subjectPrefix: z.optional(z.string()),
    from: z.optional(z.array(z.email())),
    to: z.optional(z.array(z.email())),
});

export const EwsSyncInfoSchema = z.object({
    syncState: z.nullable(z.record(z.string(), z.string())),
    folderIds: z.array(z.string()),
});

export const UifyDataSourceAuthInfoSchema = z.object({
    type: z._default(z.literal("DataSource"), "DataSource"),
    dataSourceId: z.string(),
    dataSourceEnv: z.enum(["staging", "production"]),
    organizationId: z.number(),
});

export const EntraAppRegistrationAuthInfoSchema = z.object({
    type: z.literal("ENTRA_APP"),
    tenantId: z.string(),
    clientId: z.string(),
    clientSecret: z.string(),
});

export const EwsAuthInfoSchema = z.object({
    type: z.literal("EWS"),
    host: z.string(),
    port: z.number(),
    servername: z.optional(z.string()),
    username: z.string(),
    password: z.string(),
    domain: z.optional(z.string()),
    workstation: z.optional(z.string()),
});

export const InboxAuthInfoSchema = z.discriminatedUnion("type", [
    UifyDataSourceAuthInfoSchema,
    EntraAppRegistrationAuthInfoSchema,
    EwsAuthInfoSchema,
]);

export const InboxSchema = z.object({
    id: InboxIdSchema,
    provider: ProviderSchema,
    email: z.string(),
    authInfo: InboxAuthInfoSchema,
    enabled: z.boolean(),
    syncFrom: z.nullable(z.string()),
    syncInfo: z.nullable(z.union([GmailSyncInfoSchema, ExchangeSyncInfoSchema, EwsSyncInfoSchema])),
    idempotencyKey: z.string(),
    useImpersonation: z.boolean(),
});

export const ThreadSchema = z.object({
    id: ThreadIdSchema,
    inboxId: InboxIdSchema,
    externalId: z.string(),
    versionKey: z.union([z.string(), z.number()]),
    messages: z.array(
        z.object({
            id: MessageIdSchema,
            externalId: z.string(),
            subject: z.nullable(z.string()),
            content: z.string(),
            snippet: z.nullable(z.string()),
            senderName: z.nullable(z.string()),
            senderEmail: z.nullable(z.string()),
            to: z.array(z.string()),
            cc: z.array(z.string()),
            bcc: z.array(z.string()),
            receivedAt: z.string(),
            sent: z.boolean(),
            attachmentIds: z.array(AttachmentIdSchema),
            labelIds: z.array(z.string()),
            versionKey: z.nullable(z.union([z.string(), z.number()])),
            parentFolderId: z.nullable(z.string()),
        }),
    ),
    attachments: z.record(
        AttachmentIdSchema,
        z.object({
            filename: z.string(),
            contentType: z.string(),
            size: z.number(),
            hashHex: z.string(),
            s3Bucket: z.string(),
            s3Key: z.string(),
            data: z.optional(z.string()),
        }),
    ),
});

export const AttachmentSchema = z.object({
    id: AttachmentIdSchema,
    inboxId: InboxIdSchema,
    threadId: ThreadIdSchema,
    externalId: z.nullable(z.string()),
    filename: z.string(),
    size: z.number(),
    contentType: z.string(),
    hashHex: z.string(),
    s3Bucket: z.string(),
    s3Key: z.string(),
});

export const ThreadMetaSchema = z.object({
    id: ThreadIdSchema,
    inboxId: InboxIdSchema,
    externalId: z.string(),
    subject: z.nullable(z.string()),
});

export const ExternalThreadSchema = z.object({
    id: z.string(),
    versionKey: z.union([z.string(), z.number()]),
    messages: z.array(
        z.object({
            id: z.string(),
            subject: z.nullable(z.string()),
            snippet: z.nullable(z.string()),
            to: z.array(z.string()),
            cc: z.array(z.string()),
            bcc: z.array(z.string()),
            labelIds: z.array(z.string()),
            receivedAt: z.string(),
            sent: z.boolean(),
            senderName: z.nullable(z.string()),
            senderEmail: z.nullable(z.string()),
            content: z.string(),
            parentFolderId: z.nullable(z.string()),
            versionKey: z.nullable(z.union([z.string(), z.number()])),
            internetMessageId: z.nullish(z.string()),
            inReplyTo: z.nullish(z.string()),
            conversationIndex: z.nullish(z.string()),
            conversationIndexGuid: z.nullish(z.string()),
            conversationIndexChildBlocks: z.nullish(z.array(z.string())),
            threadTopic: z.nullish(z.string()),
            references: z.nullish(z.string()),
            threadIndex: z.nullish(z.string()),
            itemId: z.nullish(z.string()),
            raw: z.nullish(SerializableSchema),
            attachments: z.array(
                z.object({
                    id: z.optional(z.string()),
                    filename: z.string(),
                    contentType: z.string(),
                    size: z.number(),
                    data: z.optional(z.string()),
                }),
            ),
        }),
    ),
});

export const AttachmentContentSchema = z.object({
    id: AttachmentIdSchema,
    inboxId: InboxIdSchema,
    filename: z.string(),
    contentType: z.string(),
    data: z.string(),
});
